/**
 * Attendance-related rendering and utility functions for Student Profile
 */
import React from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { formatDate, normalizeStatus } from './utils';

/**
 * Transform attendance API response to match expected format
 * @param {Object} apiResponse - Raw API response
 * @returns {Object} - Transformed attendance data
 */
export const transformAttendanceApiResponse = (apiResponse) => {
    // Handle the exact API response structure you showed: { success: true, data: { regular: {...}, rfid: {...}, ... } }
    if (apiResponse && apiResponse.success && apiResponse.data) {
        const attendanceData = apiResponse.data;

        // Validate that the data has the expected attendance types
        if (attendanceData.regular || attendanceData.rfid || attendanceData.transport || attendanceData.periodwise || attendanceData.professional) {
            return attendanceData;
        }
    }

    // If API response has data property directly (without success wrapper)
    if (apiResponse && apiResponse.data && !apiResponse.success) {
        return apiResponse.data;
    }

    // If API response already has the attendance types at root level
    if (apiResponse && (apiResponse.regular || apiResponse.rfid || apiResponse.transport || apiResponse.periodwise || apiResponse.professional)) {
        return apiResponse;
    }

    // Try to detect if attendance data is nested under different keys
    const possibleKeys = ['attendanceData', 'attendance', 'result'];
    for (const key of possibleKeys) {
        if (apiResponse && apiResponse[key]) {
            // If it's an array, transform it
            if (Array.isArray(apiResponse[key])) {
                return transformFlatAttendanceData(apiResponse[key]);
            }

            // If it's an object with attendance types, use it directly
            if (typeof apiResponse[key] === 'object') {
                return apiResponse[key];
            }
        }
    }

    return {};
}

/**
 * Transform flat attendance data to structured format
 * @param {Array|Object} flatData - Flat attendance data
 * @returns {Object} - Structured attendance data
 */
export const transformFlatAttendanceData = (flatData, calculateTotalsFromMonths) => {
    console.log('Transforming flat attendance data:', flatData);

    const transformedData = {
        regular: { months: [], totals: {} },
        rfid: { months: [], totals: {} },
        transport: { months: [], totals: {} },
        periodwise: { months: [], totals: {} },
        professional: { months: [], totals: {} }
    };

    if (flatData && Array.isArray(flatData)) {
        // Group by attendance type
        const groupedData = {};

        flatData.forEach(record => {
            const attendanceType = record.type || record.attendanceType || 'regular';

            if (!groupedData[attendanceType]) {
                groupedData[attendanceType] = { months: [], totals: {} };
            }

            // Transform month data with multiple possible field mappings
            const monthData = {
                month: record.month || record.monthName || record.period || '',
                noOfDays: record.noOfDays || record.totalDays || record.days || '',
                weekoffs: record.weekoffs || record.weekOffs || record.weekends || '',
                holidays: record.holidays || '',
                workingDays: record.workingDays || record.totalWorkingDays || '',
                present: record.present || record.presentDays || record.attendance || '',
                absent: record.absent || record.absentDays || '',
                notMarked: record.notMarked || record.unmarked || '',
                late: record.late || record.lateDays || '',
                halfday: record.halfday || record.halfdayDays || '',
                percentage: record.percentage || record.attendancePercentage || record.attendancePercent || '',
                hasData: (record.present > 0 || record.absent > 0 || record.presentDays > 0 || record.absentDays > 0),
                hasRefreshIcon: record.hasRefreshIcon !== false
            };

            groupedData[attendanceType].months.push(monthData);
        });

        // Copy grouped data to transformed structure
        Object.keys(groupedData).forEach(type => {
            if (transformedData[type]) {
                transformedData[type] = groupedData[type];

                // Calculate totals if not provided
                if (!transformedData[type].totals || Object.keys(transformedData[type].totals).length === 0) {
                    transformedData[type].totals = calculateTotalsFromMonths(transformedData[type].months);
                }
            }
        });

    } else if (flatData && typeof flatData === 'object') {
        // If it's a single object, assume it's regular attendance
        const monthData = {
            month: flatData.month || flatData.monthName || 'Current Month',
            noOfDays: flatData.noOfDays || flatData.totalDays || flatData.days || '',
            weekoffs: flatData.weekoffs || flatData.weekOffs || '',
            holidays: flatData.holidays || '',
            workingDays: flatData.workingDays || '',
            present: flatData.present || flatData.presentDays || '',
            absent: flatData.absent || flatData.absentDays || '',
            notMarked: flatData.notMarked || '',
            late: flatData.late || '',
            halfday: flatData.halfday || '',
            percentage: flatData.percentage || flatData.attendancePercentage || '',
            hasData: (flatData.present > 0 || flatData.absent > 0),
            hasRefreshIcon: true
        };

        transformedData.regular.months.push(monthData);
        transformedData.regular.totals = calculateTotalsFromMonths([monthData]);
    }

    return transformedData;
}

/**
 * Render attendance info view
 * @param {Object} state - Component state
 * @param {Object} handlers - Object containing all handler functions
 * @returns {JSX.Element} - Attendance info view component
 */
export const renderAttendanceInfoView = (state, handlers) => {
    const { attendanceData, activeAttendanceTab } = state;
    const { 
        handleUpdateAllAttendance, 
        handleExportAttendanceToExcel, 
        handleRefreshAttendance, 
        handleViewDailyAttendance,
        setState
    } = handlers;

    console.log('Rendering attendance view with:', {
        attendanceData,
        activeAttendanceTab,
        hasAttendanceData: !!attendanceData,
        attendanceDataKeys: Object.keys(attendanceData || {}),
        currentTabData: attendanceData?.[activeAttendanceTab]
    });

    if (!attendanceData || Object.keys(attendanceData).length === 0) {
        return (
            <div className="attendance-no-data">
                <p>No attendance data available</p>
            </div>
        );
    }

    const currentAttendanceData = attendanceData[activeAttendanceTab] || { months: [], totals: {} };
    const allMonthsData = currentAttendanceData.months || [];
    const totals = currentAttendanceData.totals || {};

    return (
        <div className="attendance-info-container">
            {/* Attendance Type Tabs and Export Button */}
            <div className="attendance-header-section">
                <div className="attendance-tabs">
                    {['regular', 'rfid', 'transport', 'periodwise', 'professional'].map((type) => (
                        <div
                            key={type}
                            className={`attendance-tab ${activeAttendanceTab === type ? 'active' : ''}`}
                            onClick={() => setState({ activeAttendanceTab: type })}
                        >
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                        </div>
                    ))}
                </div>

                <div className="attendance-header-buttons">
                    <Button
                        label="Update Attendance"
                        size="small"
                        className="attendance-action-btn update-all-btn"
                        icon="pi pi-refresh"
                        onClick={handleUpdateAllAttendance}
                    />
                    <Button
                        label="Export to Excel"
                        icon="pi pi-download"
                        size="small"
                        className="export-excel-btn"
                        onClick={handleExportAttendanceToExcel}
                    />
                </div>
            </div>

            {/* Attendance Table */}
            <div className="attendance-table-container card datatable-crud-demo">
                <DataTable
                    value={allMonthsData}
                    responsiveLayout="scroll"
                    className="attendance-datatable"
                    showGridlines
                    stripedRows
                >
                    <Column
                        field="month"
                        header="Month"
                        body={(rowData, { rowIndex }) => (
                            <div className="month-cell">
                                <span>{rowData?.month || `Month ${rowIndex + 1}`}</span>
                            </div>
                        )}
                        style={{ minWidth: '150px' }}
                    />
                    <Column
                        field="noOfDays"
                        header="Days"
                        body={(rowData) => rowData?.noOfDays || '-'}
                        style={{ textAlign: 'center', minWidth: '70px' }}
                    />
                    <Column
                        field="weekoffs"
                        header="Week Offs"
                        body={(rowData) => rowData?.weekoffs || '-'}
                        style={{ textAlign: 'center', minWidth: '100px' }}
                    />
                    <Column
                        field="holidays"
                        header="Holidays"
                        body={(rowData) => rowData?.holidays || '-'}
                        style={{ textAlign: 'center', minWidth: '100px' }}
                    />
                    <Column
                        field="workingDays"
                        header="Working Days"
                        body={(rowData) => rowData?.workingDays || '-'}
                        style={{ textAlign: 'center', minWidth: '130px' }}
                    />
                    <Column
                        field="present"
                        header="Present"
                        body={(rowData) => rowData?.present || '-'}
                        style={{ textAlign: 'center', minWidth: '100px' }}
                    />
                    <Column
                        field="absent"
                        header="Absent"
                        body={(rowData) => rowData?.absent || '-'}
                        style={{ textAlign: 'center', minWidth: '100px' }}
                    />
                    <Column
                        field="halfday"
                        header="Half Day"
                        body={(rowData) => rowData?.halfday || '-'}
                        style={{ textAlign: 'center', minWidth: '100px' }}
                    />
                    <Column
                        field="percentage"
                        header="PERCENTAGE"
                        body={(rowData) => rowData?.percentage || '-'}
                        style={{ textAlign: 'center', minWidth: '80px' }}
                    />
                    <Column
                        header="Action"
                        body={(rowData) => {
                            const hasData = rowData && (rowData.hasData !== undefined ? rowData.hasData : (rowData.present > 0 || rowData.absent > 0));
                            const hasRefreshIcon = rowData && rowData.hasRefreshIcon;

                            return (
                                <>
                                    {!hasData && hasRefreshIcon ? (
                                        <Button
                                            label="Update"
                                            size="small"
                                            className="attendance-action-btn"
                                            onClick={() => handleRefreshAttendance(rowData?.month)}
                                        />
                                    ) : hasData ? (
                                        <Button
                                            icon="pi pi-eye"
                                            label="View"
                                            size="small"
                                            className="attendance-action-btn view-btn"
                                            onClick={() => handleViewDailyAttendance(rowData?.month, activeAttendanceTab)}
                                        />
                                    ) : (
                                        <span>-</span>
                                    )}
                                </>
                            );
                        }}
                        style={{ textAlign: 'center', minWidth: '90px' }}
                    />
                </DataTable>

                {/* Totals Row */}
                <DataTable
                    value={[totals]}
                    className="attendance-totals-table"
                >
                    <Column
                        header="Month"
                        body={() => <strong>Total</strong>}
                        style={{ minWidth: '150px' }}
                    />
                    <Column
                        header="Days"
                        body={() => <strong>{totals.noOfDays || 0}</strong>}
                        style={{ textAlign: 'center', minWidth: '60px' }}
                    />
                    <Column
                        header="Week Offs"
                        body={() => <strong>{totals.weekoffs || 0}</strong>}
                        style={{ textAlign: 'center', minWidth: '100px' }}
                    />
                    <Column
                        header="Holidays"
                        body={() => <strong>{totals.holidays || 0}</strong>}
                        style={{ textAlign: 'center', minWidth: '100px' }}
                    />
                    <Column
                        header="Working Days"
                        body={() => <strong>{totals.workingDays || 0}</strong>}
                        style={{ textAlign: 'center', minWidth: '130px' }}
                    />
                    <Column
                        header="Present"
                        body={() => <strong>{totals.present || 0}</strong>}
                        style={{ textAlign: 'center', minWidth: '100px' }}
                    />
                    <Column
                        header="Absent"
                        body={() => <strong>{totals.absent || 0}</strong>}
                        style={{ textAlign: 'center', minWidth: '100px' }}
                    />
                    <Column
                        header="Half Day"
                        body={() => <strong>{totals.halfday || 0}</strong>}
                        style={{ textAlign: 'center', minWidth: '100px' }}
                    />
                    <Column
                        header="%"
                        body={() => <strong>{totals.percentage || 0}%</strong>}
                        style={{ textAlign: 'center', minWidth: '80px' }}
                    />
                    <Column
                        header="Action"
                        body={() => <strong>—</strong>}
                        style={{ textAlign: 'center', minWidth: '90px' }}
                    />
                </DataTable>
            </div>
        </div>
    );
}

/**
 * Render daily attendance dialog content
 * @param {Object} state - Component state
 * @param {Function} exportToExcel - Export function
 * @returns {JSX.Element} - Daily attendance content
 */
export const renderDailyAttendanceContent = (state, exportToExcel) => {
    const { selectedMonth, selectedAttendanceType, dailyAttendanceData, studentData } = state;

    if (!selectedMonth || !dailyAttendanceData || dailyAttendanceData.length === 0) {
        return <div>No attendance data available</div>;
    }

    // Calculate totals for the header
    const totalDays = dailyAttendanceData.length;
    const weekoffs = dailyAttendanceData.filter(d => d.status === 'Weekoff').length;
    const holidays = dailyAttendanceData.filter(d => d.status === 'Holiday').length;
    const noData = dailyAttendanceData.filter(d => d.status === 'NoData').length;
    const workingDays = totalDays - weekoffs - holidays - noData;
    const present = dailyAttendanceData.filter(d => d.status === 'Present').length;
    const absent = dailyAttendanceData.filter(d => d.status === 'Absent').length;
    const halfday = dailyAttendanceData.filter(d => d.status === 'HalfDay').length;

    return (
        <div className="daily-attendance-content">
            {/* Header Information */}
            <div className="attendance-header-info">
                <div className="header-row">
                    <span><strong>Name:</strong> {`${studentData.firstName || ''} ${studentData.lastName || ''}`.trim()}</span>
                    <span><strong>Month:</strong> {selectedMonth}</span>
                </div>
                <div className="header-row">
                    <span><strong>No of Days:</strong> {totalDays}</span>
                    <span><strong>Weekoffs:</strong> {weekoffs}</span>
                    <span><strong>Holidays:</strong> {holidays}</span>
                    <span><strong>Working Days:</strong> {workingDays}</span>
                    <span><strong>Present:</strong> {present}</span>
                    <span><strong>Absent:</strong> {absent}</span>
                    <span><strong>Half Day:</strong> {halfday}</span>
                </div>
            </div>

            {/* Excel Export Button */}
            <div className="export-section">
                <Button
                    label="Excel"
                    icon="pi pi-file-excel"
                    className="p-button-success"
                    onClick={() => exportToExcel()}
                />
            </div>

            {/* Daily Attendance Table */}
            <div className="daily-attendance-table-container card datatable-crud-demo ml-3 mr-3 mb-3">
                <DataTable
                    value={dailyAttendanceData}
                    paginator
                    rows={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    responsiveLayout="scroll"
                    className="daily-attendance-datatable"
                    stripedRows
                    rowClassName={(data) => `status-row-${data.status.toLowerCase()}`}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
                >
                    <Column
                        field="date"
                        header="Date"
                        style={{ minWidth: '120px' }}
                        className='text-center'
                    />
                    <Column
                        field="day"
                        header="Day"
                        style={{ minWidth: '120px' }}
                        className='text-center'
                    />
                    <Column
                        field="status"
                        header="Status"
                        className='text-center'
                        body={(rowData) => (
                            <span className={`status-badge status-${rowData.status.toLowerCase()}`}>
                                {rowData.status === 'Present' && 'Present'}
                                {rowData.status === 'Absent' && 'Absent'}
                                {rowData.status === 'Weekoff' && 'Weekoff'}
                                {rowData.status === 'Holiday' && 'Holiday'}
                                {rowData.status === 'Late' && 'Late'}
                                {rowData.status === 'NoData' && 'No Data'}
                                {!['Present', 'Absent', 'Weekoff', 'Holiday', 'Late', 'NoData'].includes(rowData.status) && rowData.status}
                            </span>
                        )}
                        style={{ minWidth: '150px' }}
                    />
                </DataTable>
            </div>
        </div>
    );
}
